import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { sessionInitStatusAtom } from '~/state/atoms/sessionAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    STEP_REQUIREMENT_REASON_ID,
    StepRequirement,
    WORKFLOW_STEPS,
    WorkflowStep,
} from './workflow-steps';

export interface StepAvailability {
    disabled: boolean;
    disabledReasonId?: string;
}

const AVAILABLE: StepAvailability = { disabled: false };

export interface WorkflowStepState {
    currentStep: WorkflowStep | null;
    currentStepIndex: number;
    availability: Record<string, StepAvailability>;
}

/**
 * Single source of truth for workflow step state:
 * resolves current step from the route, and evaluates
 * each step's availability against loaded data.
 *
 * Session-aware: strips /session/:id prefix from pathname
 * to match against step paths.
 */
export default function useWorkflowSteps(): WorkflowStepState {
    const { pathname } = useLocation();
    const { sessionId } = useParams<{ sessionId?: string }>();
    const primaryImage = useRecoilValue(primaryImageState);
    const sessionInitStatus = useRecoilValue(sessionInitStatusAtom);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const patient = useSelectedPatient();

    // Strip /session/:id prefix to get the effective step path
    const effectivePath = useMemo(() => {
        if (sessionId) {
            const prefix = `/session/${sessionId}`;
            if (pathname.startsWith(prefix)) {
                return pathname.slice(prefix.length) || '/';
            }
        }
        return pathname;
    }, [pathname, sessionId]);

    const { currentStep, currentStepIndex } = useMemo(() => {
        const index = WORKFLOW_STEPS.findIndex(
            (s) =>
                effectivePath === s.path ||
                effectivePath.startsWith(`${s.path}/`)
        );
        return {
            currentStepIndex: index >= 0 ? index : -1,
            currentStep: index >= 0 ? WORKFLOW_STEPS[index] : null,
        };
    }, [effectivePath]);

    const availability = useMemo(() => {
        const hasImage = !!primaryImage;
        const fulfilled: Record<StepRequirement, boolean> = {
            image: hasImage,
            secondaryImages: hasImage && !!patient && patient.images.length > 1,
            structureSet: !!currentStructureSet,
        };

        const result: Record<string, StepAvailability> = {};
        for (const step of WORKFLOW_STEPS) {
            const unmet = step.requires?.find((req) => !fulfilled[req]);
            result[step.id] = unmet
                ? {
                      disabled: true,
                      disabledReasonId: STEP_REQUIREMENT_REASON_ID[unmet],
                  }
                : AVAILABLE;
        }
        return result;
    }, [primaryImage, patient, currentStructureSet]);

    const navigate = useNavigate();
    useEffect(() => {
        // Skip the redirect while session is still initializing — the
        // image may still be downloading and we don't want to fight
        // the auto-navigation to contouring.
        if (
            currentStep &&
            availability[currentStep.id]?.disabled &&
            (!sessionId ||
                (sessionInitStatus.hasStarted && !sessionInitStatus.isLoading))
        ) {
            const setupPath = sessionId
                ? `/session/${sessionId}${WORKFLOW_STEPS[0].path}`
                : WORKFLOW_STEPS[0].path;
            navigate(setupPath, { replace: true });
        }
    }, [currentStep, availability, navigate, sessionId, sessionInitStatus]);

    return { currentStep, currentStepIndex, availability };
}
